import { type ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Combobox } from '@zillow/constellation-10';
import { MapPin } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';
import { getPlaceDetails, getPlacePredictions, type PlacePredictionOptions } from './googlePlaces';
import { PREDICTIONS_DEBOUNCE_MS } from './commuteAutocomplete.constants';
import type { CommuteDestination } from '@/src/lib/hooks/neighborhood';

type ComboboxInputGroupProps = ComponentProps<typeof Combobox.InputGroup>;

type Prediction = {
  placeId: string;
  description: string;
  primaryText: string;
  secondaryText: string;
};

type PredictionOption = {
  'aria-label': string;
  label: string;
  meta: string;
  value: string;
};

type CommuteAutocompleteProps = {
  onDestinationSelect: (destination: CommuteDestination) => void;
  placement?: 'bottom-start' | 'top-start';
  placeholder?: string;
  /** Google prediction options; defaults to named places only (no street addresses). */
  predictionOptions?: PlacePredictionOptions;
  /**
   * Seeded destination label. When provided, the input re-syncs to it whenever
   * it changes (same controlled pattern as ListingSearch's LocationCombobox),
   * so the combobox itself displays the current selection — no element swap,
   * no layout shift.
   */
  value?: string | null;
  /** Called when the Combobox's built-in clear (X) empties the input. */
  onClear?: () => void;
};

export function CommuteAutocomplete({
  onDestinationSelect,
  placement = 'bottom-start',
  placeholder,
  predictionOptions,
  value,
  onClear,
}: CommuteAutocompleteProps) {
  const t = useTranslations('hdp');
  const resolvedPlaceholder = placeholder ?? t('addDestination');
  const [inputValue, setInputValue] = useState(value ?? '');
  // Set when we invalidate the selection because the user typed over it —
  // the resulting `value` change must not clobber their in-flight text.
  const suppressValueSyncRef = useRef(false);

  // Re-sync the visible label when the parent's selection changes (seed on
  // modal reopen, Clear All, X clear). Uncontrolled callers pass no `value`.
  useEffect(() => {
    if (value === undefined) return;
    if (suppressValueSyncRef.current) {
      suppressValueSyncRef.current = false;
      return;
    }
    setInputValue(value ?? '');
  }, [value]);
  const [predictions, setPredictions] = useState<Array<Prediction>>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  const options = useMemo<Array<PredictionOption>>(
    () =>
      predictions.map((prediction) => ({
        value: prediction.placeId,
        label: prediction.primaryText,
        meta: prediction.secondaryText,
        'aria-label': [prediction.primaryText, prediction.secondaryText].filter(Boolean).join(', '),
      })),
    [predictions],
  );

  const schedulePredictions = useCallback(
    (value: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      const trimmedValue = value.trim();
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;

      if (!trimmedValue) {
        setPredictions([]);
        setIsLoading(false);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      debounceRef.current = setTimeout(async () => {
        const results = await getPlacePredictions(trimmedValue, predictionOptions);
        if (requestIdRef.current !== requestId) return;
        setPredictions(results);
        setIsLoading(false);
      }, PREDICTIONS_DEBOUNCE_MS);
    },
    [predictionOptions],
  );

  const handleInputValueChange = useCallback(
    (next: string | Array<string>) => {
      if (typeof next !== 'string') return;
      setInputValue(next);
      schedulePredictions(next);
      // The field is free-form: typing or deleting over a selected destination
      // means what's shown no longer matches the selection, so invalidate it —
      // otherwise the caller would apply stale coordinates behind edited text.
      if (onClear && value && next !== value) {
        suppressValueSyncRef.current = true;
        onClear();
      }
    },
    [schedulePredictions, value, onClear],
  );

  const handleOptionSelect = useCallback(
    async (option: unknown) => {
      if (!option || typeof option !== 'object') return;
      const value =
        typeof (option as { value?: unknown }).value === 'string' ? (option as { value: string }).value : null;
      if (!value) return;

      const prediction = predictions.find((item) => item.placeId === value);
      if (!prediction) return;

      if (debounceRef.current) clearTimeout(debounceRef.current);
      requestIdRef.current += 1;
      setInputValue(prediction.description);
      setIsOpen(false);
      setIsLoading(false);
      setPredictions([]);

      try {
        const destination = await getPlaceDetails(prediction.placeId, prediction.description);
        onDestinationSelect(destination);
      } catch {
        // Selection failed — user can try again
      }
    },
    [onDestinationSelect, predictions],
  );

  // Wired to the Combobox's built-in clear (X): the empty-input path of
  // schedulePredictions already resets the debounce/prediction state.
  const handleClear = useCallback(() => {
    setInputValue('');
    schedulePredictions('');
    onClear?.();
  }, [schedulePredictions, onClear]);

  const shouldShowDropdown = isOpen && inputValue.trim().length > 0;

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <Combobox
      appearance="input"
      autoCompleteBehavior="manual"
      freeForm
      fluidDropdown
      loading={isLoading}
      open={shouldShowDropdown}
      options={options}
      placement={placement}
      placeholder={resolvedPlaceholder}
      value={inputValue}
      aria-label={t('searchForCommuteDestination')}
      onChange={handleInputValueChange}
      onClear={handleClear}
      onOpenChange={(open) => setIsOpen(open === true)}
      onOptionSelect={handleOptionSelect}
      optionFilter={() => true}
      css={{
        width: '100%',
        '& [data-c11n-component="Combobox.Input"]': {
          textAlign: 'start',
          // body (16px), matching the other filter inputs — anything smaller
          // triggers iOS Safari's focus auto-zoom, shrinking the viewport.
          textStyle: 'body',
        },
      }}
      renderInputGroup={(groupProps) => (
        <Combobox.InputGroup {...(groupProps as ComboboxInputGroupProps)}>
          <LucideIcon icon={MapPin} size="sm" />
          {groupProps.children}
        </Combobox.InputGroup>
      )}
    />
  );
}
